import { AlertNotificationAndKey } from '@fullstory/client-api';
import { useFlags } from '@fullstory/react-hooks';
import { useCallback, useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getNotificationsLastSeen, getOrgId } from '../fullstory/selectors';
import { NotificationsRequestActions } from './actions';
import {
  getAllNotificationsError,
  getNotificationCursor,
  getHasMoreNotifications,
  getNumberUnreadNotifications,
} from './selectors';
import { getSegmentsById } from '../segments/selectors';
import { AppDispatch, AppState } from '../../types';
import { NotificationsRequests as SegmentNotificationsRequests } from '../alerts';
import { SegmentRequestActions } from '../segments';
import { Flag } from '@fullstory/proto/fs/shared/flags';

// Refetch every 5 min
const REFETCH_TIME = 5 * 60 * 1000;

export const useFetchAllNotifications = (withPolling: boolean = false) => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const cursor = useSelector(getNotificationCursor);
  const permissionError = useSelector(getAllNotificationsError);
  const moreNotifications = useSelector(getHasMoreNotifications);
  const hasPermission = useFlags().hasBit(Flag.PermViewNotifications);

  const fetch = useCallback(
    (cursorProp: string, force = false) => {
      if (!hasPermission) {
        return;
      }
      dispatch(
        NotificationsRequestActions.getAllNotifications(
          {
            orgId,
            cluster: true,
            pageSize: 20,
            cursor: cursorProp,
            filters: null,
            user: '', // The service will use the current user if unset.
          },
          force,
        ),
      );
    },
    [dispatch, orgId],
  );

  const fetchMore = useCallback(() => {
    if (!hasPermission) {
      return;
    }
    if (moreNotifications) {
      fetch(cursor);
    }
  }, [cursor, fetch, moreNotifications]);

  useEffect(() => {
    if (!hasPermission) {
      return;
    }
    let interval: NodeJS.Timeout;
    fetch('');
    /* Don't poll when the FSTA tab is not visible. This helps to avoid a bug when a user
      has two tabs open for two different orgs. The orgId from the visible tab will be used
      in the signed session the request from the hidden tab, causing a 403. */
    if (withPolling && document.visibilityState === 'visible' && !permissionError) {
      interval = setInterval(() => fetch('', true), REFETCH_TIME);
    }
    return () => clearInterval(interval);
  }, [fetch, permissionError, withPolling]);

  return { fetch, fetchMore };
};

export const useSegmentAlertNotifications = () => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId: string = useSelector(getOrgId);
  const alertsEnabled = useFlags().hasBit(Flag.FeatAlerts);

  const segmentAlertsLastSeen = useSelector(getNotificationsLastSeen);

  useEffect(() => {
    if (alertsEnabled) {
      dispatch(
        SegmentNotificationsRequests.getOverlayNotifications({
          Limit: 50,
        }),
      );
    }
    dispatch(SegmentRequestActions.getAllSegments(orgId, false));
  }, [dispatch, orgId, alertsEnabled]);

  return useSelector(
    (state: AppState) => ({
      alertNotifications: state.alerts.notificationsOverlay?.data,
      segments: getSegmentsById(state),
      segmentAlertsLastSeen,
    }),
    shallowEqual,
  );
};

export const useFetchUnreadNotificationCount = () => {
  const numberUnreadNotifications = useSelector(getNumberUnreadNotifications);
  const { alertNotifications, segmentAlertsLastSeen } = useSegmentAlertNotifications();

  const unreadSegmentAlerts = useMemo(
    () =>
      alertNotifications
        ? alertNotifications.Notifications.filter(
            (alert: AlertNotificationAndKey) =>
              new Date(alert.Notification.CreatedAt) > new Date(segmentAlertsLastSeen),
          )
        : [],
    [alertNotifications, segmentAlertsLastSeen],
  );

  return numberUnreadNotifications + unreadSegmentAlerts.length;
};
